import { Restaurant } from '@/services/restaurant';

interface DesktopStoreInfoCardProps {
  restaurant: Restaurant;
  isFavorite: boolean;
  isCheckingFavorite: boolean;
  readonly: boolean;
  calculatedRating: number;
  calculatedTotalReviews: number;
  isLoadingReviewStats: boolean;
  onFavoriteToggle: () => void;
  onReviewClick: () => void;
  onStoreInfoClick: () => void;
}

export function DesktopStoreInfoCard({
  restaurant,
  isFavorite,
  isCheckingFavorite,
  readonly,
  calculatedRating,
  calculatedTotalReviews,
  isLoadingReviewStats,
  onFavoriteToggle,
  onReviewClick,
  onStoreInfoClick
}: DesktopStoreInfoCardProps) {
  return (
    <div className="bg-white rounded-2xl border-2 border-[#86DB89] p-6 h-fit">
      {/* 매장명과 즐겨찾기 */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-4xl font-bold text-[#222222]">{restaurant.name}</h1>
        {!readonly && (
          <button
            onClick={onFavoriteToggle}
            disabled={isCheckingFavorite}
            className="hover:bg-gray-100 rounded-xl p-3 transition-colors disabled:opacity-50"
          >
            <img
              src={isFavorite ? "/icons/icon_favorites_selected.svg" : "/icons/icon_favorites.svg"}
              alt="즐겨찾기"
              className="w-8 h-8"
              style={{
                filter: isFavorite ? 'brightness(0) saturate(100%) invert(43%) sepia(96%) saturate(1352%) hue-rotate(87deg) brightness(119%) contrast(119%)' : 'brightness(0) saturate(100%) invert(20%)'
              }}
            />
          </button>
        )}
      </div>
      
      {/* 평점과 리뷰 */}
      {readonly ? (
        <div className="bg-[#F5F5F5] rounded-xl px-4 py-3 mb-4 inline-flex items-center gap-2">
          <span className="text-xl font-bold text-black">{calculatedRating.toFixed(1)}</span>
          <span className="text-xl text-black">/</span>
          <span className="text-xl text-black">5</span>
          <span className="text-xl text-black">(리뷰 {calculatedTotalReviews}개)</span>
          {isLoadingReviewStats && <span className="text-xs text-gray-500">...</span>}
        </div>
      ) : (
        <button 
          onClick={onReviewClick}
          className="bg-[#F5F5F5] rounded-xl px-4 py-3 mb-4 inline-flex items-center gap-2 hover:bg-gray-200 transition-colors"
        >
          <span className="text-xl font-bold text-black">{calculatedRating.toFixed(1)}</span>
          <span className="text-xl text-black">/</span>
          <span className="text-xl text-black">5</span>
          <span className="text-xl text-black">(리뷰 {calculatedTotalReviews}개)</span>
          {isLoadingReviewStats && <span className="text-xs text-gray-500">...</span>}
          <svg width="24" height="24" viewBox="0 0 20 20" fill="none" className="ml-2">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
      
      {/* 대표메뉴 */}
      <div className="mb-4">
        <div className="flex items-start gap-4">
          <span className="text-xl font-bold text-[#222222] min-w-[100px]">대표메뉴</span>
          <span className="text-xl text-[#222222]">
            {[restaurant.bestmenu1 || restaurant.bestmenu, restaurant.bestmenu2, restaurant.bestmenu3]
              .filter(Boolean)
              .join(' | ')}
          </span>
        </div>
      </div>
      
      {/* 주소 */}
      <div className="mb-6">
        <div className="flex items-start gap-4">
          <span className="text-xl font-bold text-[#222222] min-w-[100px]">주소</span>
          <span className="text-xl text-[#222222] leading-relaxed">{restaurant.address}</span>
        </div>
      </div>

      {/* 취소/환불 정책 */}
      <div className="mb-4">
        <div className="text-[16px] font-semibold text-gray-800 mb-2">취소/환불 정책</div>
        <div className="text-base text-gray-500 leading-relaxed">
          <p>• 마감할인: 결제 후 매장 수락 전까지 취소 가능(수락 후 불가)</p>
          <p>• 예약할인: 수령시간 2시간 전까지 취소 가능</p>
          <p>• 일반주문: 매장 수락 전까지 취소 가능(수락 후 불가)</p>
        </div>
      </div>
      
      {/* 매장 정보 더보기 */}
      <button 
        onClick={onStoreInfoClick}
        className="flex items-center gap-2 text-xl text-[#222222] hover:bg-gray-50 rounded-lg px-4 py-3 transition-colors w-full justify-center border-2 border-gray-200 hover:border-gray-300"
      >
        <span>매장 정보 더보기</span>
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}
